import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const dialogOverlayVariants = cva(
  "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  {
    variants: {
      intensity: {
        default: "bg-black/60",
        sheet: "bg-black/40",
        popover: "bg-black/30",
        none: "bg-transparent",
      },
    },
    defaultVariants: {
      intensity: "default",
    },
  },
);

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    intensity?: "default" | "sheet" | "popover" | "none";
  }
>(({ className, intensity = "default", ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(dialogOverlayVariants({ intensity }), className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentVariants = cva(
  "fixed z-50 grid w-full gap-4 border bg-background p-6 shadow-lg duration-200 " +
    "data-[state=open]:animate-in data-[state=closed]:animate-out " +
    "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
    "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 " +
    "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] " +
    "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
  {
    variants: {
      size: {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "4xl": "max-w-4xl",
        full: "max-w-full",
      },
      variant: {
        default: "rounded-lg left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] sm:rounded-xl",
        floating: "rounded-2xl bottom-0 sm:bottom-4 sm:left-4 sm:right-auto sm:translate-x-0 sm:translate-y-0",
        panel: "rounded-2xl bottom-0 sm:bottom-4 sm:left-4 sm:right-auto sm:translate-x-0 sm:translate-y-0",
        fullscreen: "inset-0 rounded-none left-0 top-0 translate-x-0 translate-y-0 max-w-full max-h-full",
      },
      position: {
        center: "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]",
        bottom: "left-0 right-0 bottom-0 translate-x-0 translate-y-0 rounded-t-[10px] sm:rounded-t-2xl",
        top: "left-0 right-0 top-0 translate-x-0 translate-y-0 rounded-b-[10px] sm:rounded-b-2xl",
      },
    },
    compoundVariants: [
      {
        variant: "floating",
        position: "bottom",
        className: "left-0 right-0 bottom-0 translate-x-0 translate-y-0 rounded-t-[10px] sm:rounded-t-2xl sm:left-4 sm:right-4 sm:bottom-4 sm:max-w-[480px]",
      },
      {
        variant: "panel",
        position: "bottom",
        className: "left-0 right-0 bottom-0 translate-x-0 translate-y-0 rounded-t-[10px] sm:rounded-t-2xl sm:left-4 sm:right-4 sm:bottom-4 sm:max-w-[480px]",
      },
    ],
    defaultVariants: {
      size: "lg",
      variant: "default",
      position: "center",
    },
  },
);

/**
 * Module-level default for the close button's screen-reader label.
 *
 * The package must not reach for a translation library (the admin panel shares
 * it and stays English), so the app that owns an i18n layer sets this once at
 * start-up and again whenever the language changes. Per-call `closeLabel`
 * still wins.
 */
let dialogCloseLabel = "Close";

export const configureDialogCloseLabel = (label: string): void => {
  dialogCloseLabel = label;
};

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  /**
   * Screen-reader label for the built-in close button. Injected rather than
   * translated here: this package is shared with the admin panel, which stays
   * English, so it must not reach for a translation library.
   */
  closeLabel?: string;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, size, variant, position, closeLabel, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay intensity={variant === "floating" || variant === "panel" ? "none" : "default"} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        dialogContentVariants({ size, variant, position }),
        "slateCardClass",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className={cn(
        "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity",
        "data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
        "hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        "disabled:pointer-events-none",
        "actionPillClass",
      )}>
        <X className="h-4 w-4" />
        <span className="sr-only">{closeLabel ?? dialogCloseLabel}</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      "pb-4 border-b",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      "pt-4 border-t",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  type DialogContentProps,
  dialogContentVariants,
};